import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { PublicEvent, clubsBookingApi } from '@/api/clubs-booking';
import {
  PublicCartItem,
  getPublicCart,
  getPublicCartTotalQuantity,
  removePublicCartItem,
  removePublicCartItemForEvent,
  savePublicCart,
} from '@/utils/publicCart';
import PublicClubHeader from '@/components/public/PublicClubHeader';
import ParentPortalShell from '@/components/parent/ParentPortalShell';
import { auth } from '@/store/authStore';
import { formatCurrencyFromCents } from '@/utils/currency';

type EventCartGroup = {
  eventId: string;
  items: PublicCartItem[];
  event: PublicEvent | null;
};

const PublicCartPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<PublicCartItem[]>([]);
  const [eventsById, setEventsById] = useState<Record<string, PublicEvent | null>>({});
  const [loading, setLoading] = useState(true);
  const [initialCartLoaded, setInitialCartLoaded] = useState(false);
  const [hasCheckedInitialRedirect, setHasCheckedInitialRedirect] = useState(false);

  useEffect(() => {
    const sync = () => setItems(getPublicCart());
    sync();
    setInitialCartLoaded(true);
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    window.addEventListener('clubsbooking-cart-updated', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
      window.removeEventListener('clubsbooking-cart-updated', sync);
    };
  }, []);

  useEffect(() => {
    const uniqueEventIds = Array.from(new Set(items.map((item) => item.eventId).filter(Boolean)));
    if (!uniqueEventIds.length) {
      setEventsById({});
      setLoading(false);
      return;
    }

    const loadEvents = async () => {
      setLoading(true);
      const entries = await Promise.all(
        uniqueEventIds.map(async (eventId) => {
          try {
            const event = await clubsBookingApi.getPublicEvent(eventId);
            return [eventId, event] as const;
          } catch {
            return [eventId, null] as const;
          }
        }),
      );
      setEventsById(Object.fromEntries(entries));
      setLoading(false);
    };
    void loadEvents();
  }, [items]);

  const groups = useMemo<EventCartGroup[]>(() => {
    const grouped = new Map<string, PublicCartItem[]>();
    items.forEach((item) => {
      const list = grouped.get(item.eventId) || [];
      list.push(item);
      grouped.set(item.eventId, list);
    });

    return Array.from(grouped.entries()).map(([eventId, eventItems]) => ({
      eventId,
      items: eventItems,
      event: eventsById[eventId] || null,
    }));
  }, [items, eventsById]);

  useEffect(() => {
    if (!initialCartLoaded || hasCheckedInitialRedirect) return;
    const uniqueEventIds = Array.from(new Set(getPublicCart().map((item) => item.eventId).filter(Boolean)));
    setHasCheckedInitialRedirect(true);
    if (uniqueEventIds.length === 1) {
      navigate(`/book/${uniqueEventIds[0]}/checkout`, { replace: true });
    }
  }, [initialCartLoaded, hasCheckedInitialRedirect, navigate]);

  const totalCount = useMemo(() => getPublicCartTotalQuantity(), [items]);

  const isParentLoggedIn = (() => {
    const user = auth.getUser();
    return !!(user.isAuthenticated && user.role === 'parent');
  })();
  const backTo = searchParams.get('backTo') || '';
  const cartBackHref = backTo && backTo.startsWith('/') && !backTo.startsWith('//') && !backTo.startsWith('/cart')
    ? backTo
    : '';

  const content = (
    <section className="w-full px-3 pb-8 pt-6 sm:px-4 md:px-8">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        {cartBackHref && (
          <Link
            to={cartBackHref}
            data-testid="cart-back-button"
            className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#1F8F5F] hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        )}
        <h1 className="text-3xl font-semibold text-slate-900">Your cart</h1>
        <p className="mt-2 text-sm text-slate-600">
          Tickets are grouped by activity. Each activity checks out separately and creates its own booking reference.
        </p>

        {loading && <p className="mt-4 text-sm text-slate-500">Loading cart...</p>}

        {!loading && totalCount === 0 && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
            <p className="text-lg font-medium text-slate-900">Your cart is empty</p>
            {/*<p className="mt-1 text-sm text-slate-600">Browse clubs and add tickets to get started.</p>
            <Link to="/clubs" className="mt-4 inline-flex rounded-lg bg-[#1F8F5F] px-4 py-2 text-sm font-medium text-white hover:bg-[#1F8F5F]">
              Browse clubs
            </Link>*/}
          </div>
        )}

        {!loading && groups.length > 0 && (
          <div className="mt-5 space-y-4">
            {groups.map((group) => {
              const event = group.event;
              const clubProfile = event?.clubProfile;
              const clubName = clubProfile?.clubName || 'Session Monkey';
              const clubInitials = clubName.slice(0, 2).toUpperCase();
              const groupTotalCents = group.items.reduce((sum, item) => {
                const ticket = event?.ticketTypes?.find((entry) => entry.id === item.ticketTypeId);
                if (!ticket) return sum;
                const sessionMultiplier = ticket.mode === 'specific_sessions' ? Math.max(1, item.sessionIds.length) : 1;
                return sum + (ticket.priceCents * sessionMultiplier);
              }, 0);

              return (
                <article key={group.eventId} data-testid={`public-cart-group-${group.eventId}`} className="rounded-xl border border-slate-200">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="w-full min-w-0 sm:w-auto sm:flex-1">
                      <div
                        data-testid={`public-cart-group-brand-${group.eventId}`}
                        className="mb-1 flex w-full items-start justify-between gap-3 sm:items-center sm:justify-start"
                      >
                        <p className="order-1 min-w-0 font-semibold text-slate-900 sm:order-2">
                          {event?.title || 'Activity unavailable'}
                        </p>
                        {clubProfile?.logoUrl ? (
                          <img
                            src={clubProfile.logoUrl}
                            alt={clubName}
                            data-testid={`public-cart-group-logo-${group.eventId}`}
                            className="order-2 h-8 w-8 shrink-0 rounded-md object-contain sm:order-1"
                          />
                        ) : (
                          <div
                            data-testid={`public-cart-group-logo-fallback-${group.eventId}`}
                            className="order-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F6FAF7] text-[11px] font-semibold text-[#1F8F5F] sm:order-1"
                          >
                            {clubInitials}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">{group.items.length} ticket line{group.items.length === 1 ? '' : 's'}</p>
                    </div>
                  </div>
                  <div className="space-y-2 p-4">
                    {group.items.map((item) => {
                      const ticket = event?.ticketTypes?.find((entry) => entry.id === item.ticketTypeId);
                      const sessions = (event?.sessions || []).filter((session) => item.sessionIds.includes(session.id));
                      return (
                        <div key={item.id} className="relative rounded-lg border border-slate-200 bg-white p-3">
                          <button
                            type="button"
                            aria-label="Remove ticket line"
                            title="Remove ticket line"
                            className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                            onClick={() => setItems(removePublicCartItem(item.id))}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          <div className="min-w-0 pr-12">
                            <p className="font-medium text-slate-900">{ticket?.name || 'Ticket'}</p>
                            <p className="text-sm text-slate-600">
                              {ticket
                                ? formatCurrencyFromCents(
                                    ticket.priceCents * (ticket.mode === 'specific_sessions' ? Math.max(1, sessions.length) : 1),
                                    event?.currency || 'GBP',
                                  )
                                : '-'}
                            </p>
                            {!!sessions.length && (
                              <ul className="mt-1 space-y-1 text-xs text-slate-500">
                                {sessions.map((session) => (
                                  <li key={session.id}>
                                    {new Date(session.startDateTime).toLocaleString('en-GB')} -{' '}
                                    {new Date(session.endDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-slate-200 px-4 py-3">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div className="text-sm">
                        <span className="font-medium text-slate-700">Activity subtotal: </span>
                        <span className="font-semibold text-slate-900">
                          {formatCurrencyFromCents(groupTotalCents, event?.currency || 'GBP')}
                        </span>
                      </div>
                      <div className="grid gap-2 sm:flex sm:justify-end">
                      <button
                        type="button"
                        data-testid={`public-cart-remove-activity-${group.eventId}`}
                        className="w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 sm:w-auto"
                        onClick={() => setItems(removePublicCartItemForEvent(group.eventId))}
                      >
                        Remove activity
                      </button>
                      <Link
                        to={`/book/${group.eventId}/checkout`}
                        data-testid={`public-cart-checkout-activity-${group.eventId}`}
                        className="w-full rounded-md bg-[#1F8F5F] px-3 py-2 text-center text-sm font-medium text-white hover:bg-[#1F8F5F] sm:w-auto"
                      >
                        Checkout activity
                      </Link>
                    </div>
                    </div>
                  </div>
                </article>
              );
            })}

            <div className="flex justify-end">
              <button
                type="button"
                className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium hover:bg-slate-50"
                onClick={() => {
                  savePublicCart([]);
                  setItems([]);
                }}
              >
                Clear cart
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );

  if (isParentLoggedIn) {
    return (
      <ParentPortalShell title="Cart" subtitle="Review tickets before checkout" showHeaderCard={false}>
        {content}
      </ParentPortalShell>
    );
  }

  return (
    <main className="min-h-screen bg-[#f3f4f8]">
      <PublicClubHeader
        clubName="Session Monkey"
        clubDescription="Review your selected tickets"
        preferSessionMonkeyFallbackLogo
        cartHref="/cart"
      />
      {content}
    </main>
  );
};

export default PublicCartPage;

